import React, { useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { normCat, CATEGORY_META, CAROUSEL_COLORS } from './constants';

export const CategoryCarousel = ({ products, categoryId, categoryName }) => {
    const ref = useRef(null);
    const autoRef = useRef(null);
    const nId = normCat(categoryId);
    const meta = CATEGORY_META[nId] || CATEGORY_META['cat-fruits'];
    const colors = CAROUSEL_COLORS[meta.color] || CAROUSEL_COLORS['emerald'];
    const Icon = meta.icon;

    const scroll = useCallback((dir) => {
        if (!ref.current) return;
        const amount = 284 * 4;
        if (dir === 'next') {
            const max = ref.current.scrollWidth - ref.current.clientWidth;
            if (ref.current.scrollLeft >= max - 10) ref.current.scrollTo({ left: 0, behavior: 'smooth' });
            else ref.current.scrollBy({ left: amount, behavior: 'smooth' });
        } else {
            if (ref.current.scrollLeft <= 10) ref.current.scrollTo({ left: ref.current.scrollWidth, behavior: 'smooth' });
            else ref.current.scrollBy({ left: -amount, behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        if (products.length > 4) {
            autoRef.current = setInterval(() => scroll('next'), 6000);
            return () => clearInterval(autoRef.current);
        }
    }, [products, scroll]);

    return (
        <div className="mb-14" data-testid={`carousel-${nId}`}>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <Badge className={`${meta.bg} ${meta.text} hover:${meta.bg} mb-2`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {meta.label}
                    </Badge>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {categoryName || meta.label}
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">{products.length} produits</p>
                </div>
                <div className="hidden md:flex items-center gap-2">
                    <button onClick={() => { clearInterval(autoRef.current); scroll('prev'); }}
                        className={`w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center ${colors.border} transition-all`}>
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button onClick={() => { clearInterval(autoRef.current); scroll('next'); }}
                        className={`w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center ${colors.border} transition-all`}>
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                </div>
            </div>
            <div ref={ref} className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onMouseEnter={() => clearInterval(autoRef.current)}
                onMouseLeave={() => { if (products.length > 4) autoRef.current = setInterval(() => scroll('next'), 6000); }}>
                {products.map((product) => (
                    <Link key={product.id} to={`/produit/${product.id}`} className="group flex-none w-[260px] snap-start">
                        <Card className="overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 rounded-2xl h-full">
                            <div className="relative aspect-square overflow-hidden bg-gray-50">
                                <img src={product.image_url} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                {product.origin && (
                                    <div className="absolute top-3 left-3">
                                        <Badge className={`${colors.badge} text-white rounded-full px-2 py-0.5 text-xs shadow`}>{product.origin}</Badge>
                                    </div>
                                )}
                            </div>
                            <CardContent className="p-4">
                                <h3 className={`font-semibold text-base text-gray-900 line-clamp-1 group-hover:${colors.accent} transition-colors`}>{product.name}</h3>
                                <p className="text-gray-400 text-sm mt-1 line-clamp-1">{product.description}</p>
                                <div className="mt-3">
                                    <span className="text-xs text-gray-400 italic">
                                        Voir le catalogue pour les prix
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                ))}
            </div>
            <div className="text-center mt-4">
                <Button asChild variant="outline" className={`rounded-full ${colors.accent} border-current hover:bg-gray-50 px-6`}>
                    <Link to={`/catalogue?category=${nId}`}>
                        Voir tous les {meta.label.toLowerCase()}
                        <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                </Button>
            </div>
        </div>
    );
};
